/**
 * iter-338k: GanttShareDialog — reusable share dialog for project Gantt charts.
 * Provides two flows:
 *   1. Generate Link — creates a tokenized public URL with copy-to-clipboard.
 *   2. Send via Email — generates the link AND emails it to the recipient.
 */
import { useState, useCallback } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Link as LinkIcon, Mail, Copy, X, Check, RefreshCw } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function GanttShareDialog({ open, onOpenChange, projectId, projectName }) {
  const [expires, setExpires] = useState(30);
  const [recipientEmail, setRecipientEmail] = useState('');
  const [recipientName, setRecipientName] = useState('');
  const [subject, setSubject] = useState('');
  const [message, setMessage] = useState('');
  const [share, setShare] = useState(null);
  const [busy, setBusy] = useState(false);
  const [copied, setCopied] = useState(false);

  const authHeaders = { Authorization: `Bearer ${localStorage.getItem('token')}` };

  const generate = useCallback(async () => {
    if (!projectId) return;
    setBusy(true);
    try {
      const r = await axios.post(`${API}/projects/${projectId}/gantt-share`,
        { expires_in_days: Number(expires) || 30 }, { headers: authHeaders });
      setShare(r.data);
      toast.success('Share link generated');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to generate link');
    } finally {
      setBusy(false);
    }
  }, [projectId, expires]);

  const sendEmail = async () => {
    if (!recipientEmail) { toast.error('Enter recipient email'); return; }
    if (!projectId) return;
    setBusy(true);
    try {
      const r = await axios.post(`${API}/projects/${projectId}/gantt-share/email`, {
        expires_in_days: Number(expires) || 30,
        recipient_email: recipientEmail,
        recipient_name: recipientName || undefined,
        subject: subject || undefined,
        message: message || undefined,
      }, { headers: authHeaders });
      setShare(r.data);
      if (r.data.emailed) toast.success(`Sent to ${recipientEmail}`);
      else toast.warning('Link generated but email failed: ' + (r.data.email_error || 'unknown'));
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to send');
    } finally {
      setBusy(false);
    }
  };

  const copyLink = async () => {
    if (!share?.public_url) return;
    const url = share.public_url.startsWith('http') ? share.public_url : `${window.location.origin}${share.public_url}`;
    try {
      await navigator.clipboard.writeText(url);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
      toast.success('Link copied');
    } catch {
      toast.error('Could not copy — select the link manually');
    }
  };

  const revoke = async () => {
    if (!projectId) return;
    if (!window.confirm('Revoke ALL active share links for this project?')) return;
    setBusy(true);
    try {
      const r = await axios.post(`${API}/projects/${projectId}/gantt-share/revoke`, {}, { headers: authHeaders });
      toast.success(`Revoked ${r.data.revoked_count || 0} link(s)`);
      setShare(null);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Revoke failed');
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl" data-testid="gantt-share-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2"><LinkIcon size={18} className="text-blue-700" />Share Project Gantt</DialogTitle>
          <DialogDescription>{projectName || 'Project'} — Generate a read-only public Gantt URL or email it to a client / vendor.</DialogDescription>
        </DialogHeader>

        <div className="space-y-4 mt-2">
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label className="text-xs">Expires in (days)</Label>
              <Input type="number" min={1} max={365} value={expires} onChange={(e) => setExpires(e.target.value)} className="h-9 text-sm" data-testid="gantt-share-expires" />
            </div>
            <div className="flex items-end">
              <Button onClick={generate} disabled={busy} className="h-9 w-full" data-testid="gantt-share-generate-btn">
                <RefreshCw size={14} className={`mr-1 ${busy ? 'animate-spin' : ''}`} />Generate Link
              </Button>
            </div>
          </div>

          {share?.public_url && (
            <div className="rounded border-2 border-blue-200 bg-blue-50/40 p-3" data-testid="gantt-share-link-block">
              <div className="text-[11px] text-stone-500 mb-1">Public URL — expires {share.expires_at ? share.expires_at.slice(0, 10) : '—'}</div>
              <div className="flex items-center gap-2">
                <Input
                  readOnly
                  value={share.public_url.startsWith('http') ? share.public_url : `${window.location.origin}${share.public_url}`}
                  className="h-9 text-xs font-mono bg-white"
                  data-testid="gantt-share-link-input"
                  onFocus={(e) => e.target.select()}
                />
                <Button size="sm" variant="outline" onClick={copyLink} data-testid="gantt-share-copy-btn">
                  {copied ? <Check size={14} className="text-emerald-600" /> : <Copy size={14} />}
                </Button>
              </div>
            </div>
          )}

          <div className="border-t border-stone-200 pt-3">
            <div className="flex items-center gap-2 mb-2">
              <Mail size={14} className="text-stone-500" />
              <span className="text-sm font-semibold">Send via Email (optional)</span>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="text-xs">Recipient Email</Label>
                <Input value={recipientEmail} onChange={(e) => setRecipientEmail(e.target.value)} placeholder="client@example.com" className="h-9 text-sm" data-testid="gantt-share-email" />
              </div>
              <div>
                <Label className="text-xs">Recipient Name (optional)</Label>
                <Input value={recipientName} onChange={(e) => setRecipientName(e.target.value)} placeholder="John" className="h-9 text-sm" data-testid="gantt-share-name" />
              </div>
            </div>
            <div className="mt-2">
              <Label className="text-xs">Subject (optional)</Label>
              <Input value={subject} onChange={(e) => setSubject(e.target.value)} placeholder={`Project Gantt — ${projectName || ''}`} className="h-9 text-sm" />
            </div>
            <div className="mt-2">
              <Label className="text-xs">Message (optional)</Label>
              <Textarea rows={3} value={message} onChange={(e) => setMessage(e.target.value)} placeholder="Hi, sharing the latest project timeline…" className="text-sm" />
            </div>
            <div className="mt-3 flex justify-end">
              <Button onClick={sendEmail} disabled={busy || !recipientEmail} data-testid="gantt-share-send-btn">
                <Mail size={14} className="mr-1" />Send Link
              </Button>
            </div>
          </div>
        </div>

        <div className="flex items-center justify-between mt-2 pt-3 border-t border-stone-200">
          <Button variant="outline" size="sm" onClick={revoke} disabled={busy} className="text-red-700 border-red-200 hover:bg-red-50" data-testid="gantt-share-revoke-btn">Revoke All</Button>
          <Button variant="outline" onClick={() => onOpenChange(false)} data-testid="gantt-share-close-btn"><X size={14} className="mr-1" />Close</Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
